
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type DecalShaderUniforms = {
	uModelMatrix: WebGLUniformLocation,
	uViewProjectionMatrix: WebGLUniformLocation,
	uDepthMap: WebGLUniformLocation,
	uInvViewProjection: WebGLUniformLocation,
	uDecalTexture: WebGLUniformLocation,
	uInverseModelMatrix: WebGLUniformLocation,
	uViewportSize: WebGLUniformLocation,
	uOpacity: WebGLUniformLocation
};

export type DecalShaderAttributes = {
	aVertexPosition: number
};

export type DecalShaderSettings = string[];

export class DecalShader extends ShaderBase<DecalShaderUniforms, DecalShaderAttributes> {

    private static fragmentShaderSource = `
out vec4 fragColor;

uniform sampler2D uDepthMap;
uniform mat4 uInvViewProjection;
uniform sampler2D uDecalTexture;
uniform mat4 uInverseModelMatrix;
uniform vec2 uViewportSize;
uniform float uOpacity;

// --- begin: world_pos_from_depth.glsl ---
// Reconstruct world / view position from a 24-bit depth sample, screen-space
// UV, and an inverse projection matrix. Replaces an explicit RGBA32F position
// attachment in the G-buffer (~16 bytes/pixel saved).
//
// Cleared depth (= 1.0) means "no geometry was drawn" — callers should discard
// or treat this as background BEFORE calling these functions. The reconstruction
// itself still works at depth == 1.0 (it returns the far-plane point), but the
// result is rarely useful for lighting/AO/decals.

// Inputs:
//   depth              — raw [0,1] value from texture(uDepthMap, uv).r
//   uv                 — screen-space UV (vTexCoord, in [0,1])
//   invViewProjection  — inverse of (projection * view) for the current camera
vec3 worldPosFromDepth(float depth, vec2 uv, mat4 invViewProjection) {
	vec4 ndc = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
	vec4 world = invViewProjection * ndc;
	return world.xyz / world.w;
}

// View-space variant — pass invProjection (inverse of camera.projectionMatrix)
// when you only need view space. Skips the worldPos → viewPos round-trip,
// which matters for SSAO that samples 16+ positions per fragment.
vec3 viewPosFromDepth(float depth, vec2 uv, mat4 invProjection) {
	vec4 ndc = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
	vec4 view = invProjection * ndc;
	return view.xyz / view.w;
}

// --- end: world_pos_from_depth.glsl ---

void main(void) {
	// Screen-space UV from fragment position
	vec2 screenUV = gl_FragCoord.xy / uViewportSize;

	// Read depth from G-buffer; cleared depth (= 1.0) means no opaque surface.
	float depth = texture(uDepthMap, screenUV).r;
	if (depth >= 1.0) discard;

	// Reconstruct the underlying surface's world position from depth.
	vec3 worldPos = worldPosFromDepth(depth, screenUV, uInvViewProjection);

	// Transform world position into decal's local space
	vec3 localPos = (uInverseModelMatrix * vec4(worldPos, 1.0)).xyz;

	// Check if inside unit cube [-0.5, 0.5]
	if (abs(localPos.x) > 0.5 || abs(localPos.y) > 0.5 || abs(localPos.z) > 0.5) discard;

	// Sample decal texture using local XZ as UV (Y is the projection axis)
	vec2 decalUV = localPos.xz + 0.5;
	vec4 decalColor = texture(uDecalTexture, decalUV);

	float alpha = decalColor.a * uOpacity;
	if (alpha < 0.01) discard;
	fragColor = vec4(decalColor.rgb, alpha);
}
`;

    private static vertexShaderSource = `
uniform mat4 uModelMatrix;
uniform mat4 uViewProjectionMatrix;

layout(location = 0) in vec3 aVertexPosition;

void main(void) {
	vec4 worldPos = uModelMatrix * vec4(aVertexPosition, 1.0);
	gl_Position = uViewProjectionMatrix * worldPos;
}
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: DecalShaderUniformsContainer;
    public attributesContainer: DecalShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: DecalShaderSettings) {
        super(ctx, DecalShader.fragmentShaderSource, DecalShader.vertexShaderSource, settings);
        this.uniformsContainer = new DecalShaderUniformsContainer(ctx, this);
        this.attributesContainer = new DecalShaderShaderAttributesContainer(ctx, this);
    }
}

class DecalShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: DecalShader) {
        super(ctx);
    }

    public setModelMatrix(value: mat4 | number[]) : DecalShaderUniformsContainer {
        this.setValue('uModelMatrix', this.shader.uniforms.uModelMatrix, value, 'mat4');
        return this;
    }

    public setViewProjectionMatrix(value: mat4 | number[]) : DecalShaderUniformsContainer {
        this.setValue('uViewProjectionMatrix', this.shader.uniforms.uViewProjectionMatrix, value, 'mat4');
        return this;
    }

    public setDepthMap(texture: Texture) : DecalShaderUniformsContainer {
        this.setTextureValue('uDepthMap', this.shader.uniforms.uDepthMap, texture, 'sampler2D');
        return this;
    }

    public setInvViewProjection(value: mat4 | number[]) : DecalShaderUniformsContainer {
        this.setValue('uInvViewProjection', this.shader.uniforms.uInvViewProjection, value, 'mat4');
        return this;
    }

    public setDecalTexture(texture: Texture) : DecalShaderUniformsContainer {
        this.setTextureValue('uDecalTexture', this.shader.uniforms.uDecalTexture, texture, 'sampler2D');
        return this;
    }

    public setInverseModelMatrix(value: mat4 | number[]) : DecalShaderUniformsContainer {
        this.setValue('uInverseModelMatrix', this.shader.uniforms.uInverseModelMatrix, value, 'mat4');
        return this;
    }

    public setViewportSize(value: vec2 | number[]) : DecalShaderUniformsContainer {
        this.setValue('uViewportSize', this.shader.uniforms.uViewportSize, value, 'vec2');
        return this;
    }

    public setOpacity(value: number) : DecalShaderUniformsContainer {
        this.setValue('uOpacity', this.shader.uniforms.uOpacity, value, 'float');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uDepthMap', uniform: this.shader.uniforms.uDepthMap, dataType: 'sampler2D' },
			{ name: 'uDecalTexture', uniform: this.shader.uniforms.uDecalTexture, dataType: 'sampler2D' }
        ];
    }
}

class DecalShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: DecalShader) {
        super(ctx);
    }

    public setVertexPosition(settings: { length: number, offset: number, itemSize: number }) : DecalShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}